import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react'
import { Send, Square } from 'lucide-react'
import DynamicPrompts from './DynamicPrompts'
import { useRotatingSuggestion } from '../hooks/useRotatingSuggestion'

const InputArea = forwardRef(({ 
  onSendMessage, 
  onStopGeneration, 
  isLoading, 
  onPromptSelect, 
  contextData, 
  hasMessages = false,
  league = 'nba',
  activeTab = 'home', // 'home' | 'pulse'
  pinnedGameId = null,
}, ref) => {
  const [input, setInput] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef(null)
  const wrapperRef = useRef(null)
  
  // Rotating suggestions
  const {
    currentSuggestion,
    isTransitioning,
    freeze,
    unfreeze,
  } = useRotatingSuggestion({
    league,
    context: activeTab === 'feed' ? 'pulse' : 'home',
    pinnedGameId,
    enabled: !hasMessages && !input, // Only rotate when idle
  })

  useImperativeHandle(ref, () => ({
    setStarterPrompt: (text) => {
      setInput(text)
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus()
          textareaRef.current.setSelectionRange(text.length, text.length)
        }
      }, 0)
    }
  }))

  const handleSend = () => {
    if (input.trim()) {
      onSendMessage(input)
      setInput('')
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow Shift+Enter for new line - default behavior
        return
      } else {
        // Enter alone sends message
        e.preventDefault()
        handleSend()
      }
    }
  }

  const hasInput = input.trim().length > 0

  const autosize = () => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = '0px'
    const next = Math.min(el.scrollHeight, 180)
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight > 180 ? 'auto' : 'hidden'
  }

  useEffect(() => {
    autosize()
  }, [input])

  return (
    <div style={{ backgroundColor: '#FAFAFA' }}>
      <div 
        className="px-4 sm:px-8 lg:px-12 py-3" 
        style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
      >
        <div className="max-w-[720px] mx-auto flex flex-col gap-2">
          {/* Quick Topics - visible on all devices, adapts based on conversation state */}
          <DynamicPrompts onSelectPrompt={onPromptSelect} contextData={contextData} compact={true} hasMessages={hasMessages} />

          {/* Input container - Premium composer */}
          <div 
            ref={wrapperRef}
            className="flex items-center gap-3"
            style={{ 
              border: '1px solid rgba(17,24,39,0.08)',
              borderRadius: '16px',
              padding: '0px 12px 0px 16px',
              backgroundColor: '#FFFFFF',
              boxShadow: isFocused 
                ? '0 8px 24px rgba(17,24,39,0.10)' 
                : '0 4px 16px rgba(17,24,39,0.08)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              minHeight: '52px',
            }}
          >
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { setIsFocused(true); freeze(); }}
              onBlur={() => { setIsFocused(false); unfreeze(); }}
              onMouseEnter={freeze}
              onMouseLeave={() => !isFocused && unfreeze()}
              placeholder={hasMessages ? "Reply..." : "Ask Orb about games, odds, or matchups…"}
              className="flex-1 bg-transparent focus:outline-none"
              style={{ 
                color: '#111827',
                fontSize: '15px',
                fontWeight: '400',
                lineHeight: '1.5',
                minHeight: '22px',
                maxHeight: '200px',
                resize: 'none',
                overflowY: 'hidden',
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: '0px',
                paddingRight: '0px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
                letterSpacing: '-0.3px',
                verticalAlign: 'middle',
                margin: '0px',
                border: 'none',
              }}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              rows={1}
            />
            
            {/* Send/Stop button - pink icon with gray circle background */}
            {isLoading ? (
              <button
                onClick={onStopGeneration}
                className="flex-shrink-0 flex items-center justify-center transition-all duration-200 hover:scale-110"
                style={{ 
                  background: '#F3F4F6',
                  border: 'none',
                  color: '#FF4D6D',
                  cursor: 'pointer',
                  padding: '0',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Stop generating"
              >
                <Square size={16} fill="currentColor" strokeWidth={0} />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!hasInput}
                className="flex-shrink-0 flex items-center justify-center transition-all duration-200"
                style={{ 
                  background: hasInput ? '#F3F4F6' : 'transparent',
                  border: 'none',
                  color: '#FF4D6D',
                  cursor: hasInput ? 'pointer' : 'default',
                  padding: '0',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: hasInput ? 1 : 0.4,
                }}
                onMouseEnter={(e) => {
                  if (hasInput) e.currentTarget.style.backgroundColor = '#E5E7EB'
                }}
                onMouseLeave={(e) => {
                  if (hasInput) e.currentTarget.style.backgroundColor = '#F3F4F6'
                }}
              >
                <Send size={18} strokeWidth={2.5} />
              </button>
            )}
          </div>

          <div style={{ marginTop: '4px', display: 'flex', justifyContent: 'flex-end', paddingRight: '16px' }}>
            <div className="text-[10px]" style={{ color: 'rgba(17,24,39,0.30)', letterSpacing: '-0.2px' }}>
              Enter to send, Shift+Enter for new line
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

InputArea.displayName = 'InputArea'
export default InputArea
